import {
  Cpu,
  HardDrive,
  Laptop,
  Monitor,
  Network,
  Package,
  Printer,
  Server,
  Smartphone,
  Wrench,
  type LucideIcon,
} from "lucide-react";

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  laptop: Laptop,
  desktop: Monitor,
  server: Server,
  gpu: Cpu,
  monitor: Monitor,
  networking: Network,
  storage_device: HardDrive,
  mobile_device: Smartphone,
  peripheral: Printer,
  tool: Wrench,
  other: Package,
};

export function categoryIcon(value: string): LucideIcon {
  return CATEGORY_ICONS[value] ?? Package;
}

const CATEGORY_COLORS: Record<string, string> = {
  laptop: "hsl(210,100%,40%)",
  desktop: "hsl(262,83%,45%)",
  server: "hsl(35,90%,35%)",
  gpu: "hsl(0,70%,45%)",
  monitor: "hsl(210,100%,40%)",
  networking: "hsl(180,72%,30%)",
  storage_device: "hsl(38,92%,40%)",
  mobile_device: "hsl(262,83%,45%)",
  peripheral: "hsl(215,16%,40%)",
  tool: "hsl(215,16%,40%)",
  other: "hsl(215,16%,40%)",
};

export function categoryColor(value: string): string {
  return CATEGORY_COLORS[value] ?? "hsl(215,16%,40%)";
}

const CONDITION_COLORS: Record<string, { bg: string; fg: string }> = {
  new: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
  good: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)" },
  fair: { bg: "hsl(38,90%,92%)", fg: "hsl(35,90%,32%)" },
  damaged: { bg: "hsl(0,70%,92%)", fg: "hsl(0,70%,40%)" },
  retired: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)" },
};

export function ConditionBadge({ value }: { value: string }) {
  const c = CONDITION_COLORS[value] ?? CONDITION_COLORS.good;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize"
      style={{ background: c.bg, color: c.fg }}
    >
      {value}
    </span>
  );
}

const STATUS_COLORS: Record<string, { bg: string; fg: string; label: string }> =
  {
    available: {
      bg: "hsl(142,55%,92%)",
      fg: "hsl(142,72%,28%)",
      label: "Available",
    },
    in_use: {
      bg: "hsl(210,55%,92%)",
      fg: "hsl(210,100%,28%)",
      label: "In Use",
    },
    under_repair: {
      bg: "hsl(38,90%,92%)",
      fg: "hsl(35,90%,32%)",
      label: "Under Repair",
    },
    retired: {
      bg: "hsl(210,16%,93%)",
      fg: "hsl(215,16%,40%)",
      label: "Retired",
    },
    lost: { bg: "hsl(0,70%,92%)", fg: "hsl(0,70%,40%)", label: "Lost" },
  };

export function StatusBadge({ value }: { value: string }) {
  const s = STATUS_COLORS[value] ?? STATUS_COLORS.available;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

const MAINT_TYPE_COLORS: Record<string, { bg: string; fg: string }> = {
  repair: { bg: "hsl(0,70%,92%)", fg: "hsl(0,70%,40%)" },
  service: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
  upgrade: { bg: "hsl(262,55%,92%)", fg: "hsl(262,83%,38%)" },
  inspection: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)" },
};

export function MaintenanceTypeBadge({ value }: { value: string }) {
  const c = MAINT_TYPE_COLORS[value] ?? MAINT_TYPE_COLORS.service;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize"
      style={{ background: c.bg, color: c.fg }}
    >
      {value}
    </span>
  );
}

export function warrantyCell(iso: string | null): {
  text: string;
  color: string;
  pill: string | null;
} {
  if (!iso) return { text: "—", color: "hsl(215,16%,47%)", pill: null };
  const d = new Date(iso);
  const diff = d.getTime() - Date.now();
  const days = Math.floor(diff / (24 * 3600 * 1000));
  const text = d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  if (diff < 0)
    return { text, color: "hsl(0,70%,40%)", pill: "Expired" };
  if (days <= 30)
    return { text, color: "hsl(35,90%,32%)", pill: "Expiring soon" };
  return { text, color: "hsl(215,16%,47%)", pill: null };
}

export function formatCurrency(
  amount: number | null,
  currency: string | null,
): string {
  if (amount === null || amount === undefined) return "—";
  try {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency: currency ?? "AED",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency ?? "AED"} ${amount.toLocaleString()}`;
  }
}
